// react-i18next wiring.
//
// Committed 1.0 languages are English, Dutch and French (CLAUDE.md /
// v1-scope.md). Catalogues are i18next JSON files in locales/, one per
// language, maintained in Weblate (docs/dev/translations.md). Every
// file there is picked up at build time: adding a language needs no
// code change.
//
// The UI language is the user's saved choice when they have one
// (``applyUserLanguage``), else the browser's preference list
// (``navigator.languages``), matching on the base tag (``nl-BE`` →
// ``nl``), falling back to English.

import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import type { TranslationStrings } from "./en";

const catalogues = import.meta.glob<TranslationStrings>("./locales/*.json", {
  eager: true,
  import: "default",
});

export const resources: Record<string, { translation: TranslationStrings }> =
  Object.fromEntries(
    Object.entries(catalogues).map(([path, translation]) => [
      // Weblate names files like pt_BR.json; i18next and the browser
      // use pt-BR. Keys are kept lower-case (see lowerCaseLng below).
      path
        .replace(/^.*\/(.+)\.json$/, "$1")
        .replace("_", "-")
        .toLowerCase(),
      { translation },
    ]),
  );

export type SupportedLanguage = string;

export const supportedLanguages = Object.keys(resources) as SupportedLanguage[];

export const fallbackLanguage: SupportedLanguage = "en";

function isSupported(lng: string): boolean {
  return (supportedLanguages as string[]).includes(lng);
}

/**
 * First supported language in the browser's preference list
 * (exact tag first, then its base language), else English.
 */
export function detectLanguage(
  preferred: readonly string[] = typeof navigator !== "undefined"
    ? navigator.languages?.length
      ? navigator.languages
      : [navigator.language]
    : [],
): SupportedLanguage {
  for (const tag of preferred) {
    if (!tag) continue;
    const lower = tag.toLowerCase();
    if (isSupported(lower)) return lower;
    const base = lower.split("-")[0] ?? "";
    if (isSupported(base)) return base;
  }
  return fallbackLanguage;
}

// Keep <html lang> in sync so assistive tech pronounces the UI in the
// right language (WCAG 2.2 — 3.1.1 Language of Page).
i18n.on("languageChanged", (lng) => {
  if (typeof document !== "undefined") {
    document.documentElement.lang = lng;
  }
});

void i18n.use(initReactI18next).init({
  resources,
  lng: detectLanguage(),
  fallbackLng: fallbackLanguage,
  supportedLngs: supportedLanguages,
  lowerCaseLng: true,
  // An empty (untranslated) string falls back to English.
  returnEmptyString: false,
  interpolation: { escapeValue: false },
});

/**
 * Switch to the signed-in user's saved language, or back to the
 * browser's preference when they have none (``null``) or it isn't
 * available in this build.
 */
export function applyUserLanguage(locale: string | null | undefined): void {
  const lng = locale && isSupported(locale) ? locale : detectLanguage();
  if (i18n.language !== lng) void i18n.changeLanguage(lng);
}

/** Each available language, named in itself ("Nederlands", "français"). */
export function languageOptions(): Array<{ code: string; name: string }> {
  return supportedLanguages
    .map((code) => {
      let name = code;
      try {
        name =
          new Intl.DisplayNames([code], { type: "language" }).of(code) ?? code;
      } catch {
        // Unknown to this browser's Intl data: show the code.
      }
      return {
        code,
        name: name.charAt(0).toLocaleUpperCase(code) + name.slice(1),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

export default i18n;
